import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import DealStageSelect, { stageLabel } from '../DealStageSelect';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import RichTextEditor from '../RichTextEditor';
import { Label } from '../ui/label';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from '../ui/select';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { DatePickerField } from '../ui/date-picker-field';
import { Plus, Trash2, CalendarDays, Ban, Globe2, LifeBuoy, Sparkles, Pencil } from 'lucide-react';
import { getRegionSuggestions, getProjectYear } from './holidaySuggestions';
import { SalesLeadSelector } from './SalesLeadSelector';
import { CSLeadSelector } from './CSLeadSelector';

const HOLIDAY_REGIONS = [
  { value: 'NONE', label: 'None / Skip auto-applied holidays' },
  { value: 'US', label: 'United States' },
  { value: 'CA', label: 'Canada' },
  { value: 'UK', label: 'United Kingdom' },
  { value: 'AU', label: 'Australia' },
  { value: 'IN', label: 'India' },
  { value: 'DE', label: 'Germany' },
  { value: 'FR', label: 'France' },
  { value: 'JP', label: 'Japan' },
  { value: 'EU', label: 'European Union (general)' },
  { value: 'GLOBAL', label: 'Multi-region (general)' },
];

// Compute calendar / working day length of a freeze window. Returns
// `null` for invalid or partial date ranges so the caller can hide the
// chip cleanly. End date is INCLUSIVE per Project Conditions semantics
// (a 1-day break with start=end blocks that day fully).
const computeRangeDuration = (start, end) => {
  if (!start || !end) return null;
  const sd = new Date(`${start}T00:00:00`);
  const ed = new Date(`${end}T00:00:00`);
  if (Number.isNaN(sd.getTime()) || Number.isNaN(ed.getTime())) return null;
  if (ed < sd) return null;
  const calDays = Math.round((ed - sd) / 86400000) + 1;
  let workingDays = 0;
  for (let d = new Date(sd); d <= ed; d.setDate(d.getDate() + 1)) {
    const dow = d.getDay();
    if (dow !== 0 && dow !== 6) workingDays += 1;
  }
  // Format the secondary label into the most natural unit. Weeks once
  // the range crosses ~10 calendar days, otherwise calendar days.
  const weeks = calDays / 7;
  const secondary = calDays >= 10
    ? `${weeks % 1 === 0 ? weeks : weeks.toFixed(1)} cal weeks`
    : `${calDays} cal days`;
  const primary = `${workingDays} working day${workingDays === 1 ? '' : 's'}`;
  return { workingDays, calDays, primary, secondary, label: `${primary} · ${secondary}` };
};

export const EMPTY_CONDITIONS = {
  goal_duration_weeks: null,
  goal_start_date: null,
  goal_go_live_date: null,
  hypercare_weeks: null,
  breaks: [],
  blackouts: [],
  holiday_region: null,
  description: '',
};

const newId = () => `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

/**
 * ProjectConditionsForm — controlled editor for the unidirectional flow's
 * project_conditions sub-document. Used inside the /estimator/new wizard
 * (Batch B) and surfaced as a read-only summary on the Planner page (Batch C).
 *
 * Each break can scope which components honor it (`applies_to`):
 *   - `'all'`       → every component pauses (default)
 *   - `'none'`      → calendar marker only; no scheduling impact
 *   - `'specific'`  → only chosen `component_names[]` honor it
 *
 * The optional `availableComponents[]` prop populates the multi-select
 * shown when `applies_to === 'specific'`. Pass it from EstimatorPage /
 * EditConditionsDialog so the choices reflect the live estimate.
 */
export function ProjectConditionsForm({ value, onChange, disabled = false, availableComponents = [], highlightBreakId = null, meta = null, estimateId = null, salesLead = null, onSalesLeadChange = null, csLead = null, onCSLeadChange = null, entityContext = null, dealStage = null }) {
  const [stageDate, setStageDate] = useState('');
  const [editIdx, setEditIdx] = useState(null);
  const [editDate, setEditDate] = useState('');
  const saveDate = async (idx) => {
    try {
      const r = await axios.patch(`${process.env.REACT_APP_BACKEND_URL}/api/estimates/${estimateId}/deal-stage/history/${idx}`, { at: editDate }, { withCredentials: true });
      toast.success('Promotion date updated');
      setEditIdx(null);
      dealStage?.onChange?.({ deal_stage: dealStage.stage, deal_stage_lost: dealStage.lost, history: r.data.history });
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not update the date'); }
  };
  const v = value || EMPTY_CONDITIONS;

  const update = (patch) => onChange({ ...v, ...patch });

  // v378 — Entity & Region live at estimate level (not inside the conditions
  // JSON); the section renders only when the host passes an entityContext.
  const _selectedEntity = entityContext
    ? (entityContext.entities || []).find(e => e.id === entityContext.entityId) || null
    : null;
  const _entityRegions = _selectedEntity
    ? (_selectedEntity.regions && _selectedEntity.regions.length
        ? _selectedEntity.regions
        : (_selectedEntity.region ? [_selectedEntity.region] : []))
    : [];

  // v258.9 — auto-derive Target Go-Live = Target Start + Goal Duration.
  // Fires whenever the user changes EITHER start OR duration. Returns an
  // ISO 'YYYY-MM-DD' string (or null when inputs are incomplete).
  // Matches the backend's calendar-span math (`round(days / 7)` weeks),
  // so duration*7 days produces exactly `duration` calendar weeks between
  // start and the derived go-live.
  const deriveGoLive = (startIso, durationWeeks) => {
    if (!startIso || !durationWeeks || durationWeeks < 1) return null;
    const dt = new Date(startIso + 'T00:00:00Z');
    if (Number.isNaN(dt.getTime())) return null;
    const goLive = new Date(dt.getTime() + durationWeeks * 7 * 24 * 60 * 60 * 1000);
    return goLive.toISOString().slice(0, 10);
  };

  const updateStart = (newStart) => {
    const patch = { goal_start_date: newStart || null };
    const derived = deriveGoLive(newStart, v.goal_duration_weeks);
    if (derived) patch.goal_go_live_date = derived;
    update(patch);
  };

  const updateDuration = (newDuration) => {
    const patch = { goal_duration_weeks: newDuration };
    const derived = deriveGoLive(v.goal_start_date, newDuration);
    if (derived) patch.goal_go_live_date = derived;
    update(patch);
  };

  const addItem = (kind) => {
    const base = {
      id: newId(), label: '', start_date: '', end_date: '',
    };
    if (kind === 'breaks') {
      base.applies_to = 'all';
      base.component_names = [];
    }
    const next = [...(v[kind] || []), base];
    update({ [kind]: next });
  };

  const updateItem = (kind, idx, field, val) => {
    const list = [...(v[kind] || [])];
    list[idx] = { ...list[idx], [field]: val };
    update({ [kind]: list });
  };

  const removeItem = (kind, idx) => {
    const list = [...(v[kind] || [])];
    list.splice(idx, 1);
    update({ [kind]: list });
  };

  const region = v.holiday_region || 'NONE';

  return (
    <div className="space-y-6" data-testid="project-conditions-form">
      {/* ── Entity & Region (v378) — sets currency, SOW contracting entity
          and the regional sell/cost rate cards. */}
      {entityContext && (entityContext.entities || []).length > 0 && (
        <div className={`border rounded-lg p-3 ${entityContext.entityId ? 'border-slate-200 bg-slate-50/60' : 'border-amber-300 bg-amber-50/60'}`} data-testid="conditions-entity-section">
          <div className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-2 flex items-center gap-2">Selling Entity &amp; Region <span className="text-rose-500 normal-case tracking-normal font-semibold">required</span>
            {!entityContext.entityId && <span className="text-[10px] normal-case tracking-normal font-normal text-amber-700" data-testid="conditions-entity-required-note">— sets the price book; pricing can't start until this is chosen</span>}
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[10px] font-medium text-slate-500 uppercase tracking-wider">Contracting Entity</label>
              <select
                value={entityContext.entityId || ''}
                onChange={e => entityContext.onChange(e.target.value || null, null)}
                disabled={disabled}
                className="w-full h-8 mt-0.5 text-xs border border-slate-200 rounded-md px-2 bg-white"
                data-testid="conditions-entity-select"
              >
                <option value="">— None —</option>
                {entityContext.entities.map(en => (
                  <option key={en.id} value={en.id}>
                    {en.code || en.id}{en.default_currency_code ? ` (${en.default_currency_code})` : ''} — {en.name}
                  </option>
                ))}
              </select>
              <p className="text-[10px] text-slate-400 mt-1">Sets the estimate currency and the SOW contracting entity.</p>
            </div>
            <div>
              <label className="text-[10px] font-medium text-slate-500 uppercase tracking-wider">Region</label>
              <select
                value={entityContext.region || ''}
                onChange={e => entityContext.onChange(entityContext.entityId, e.target.value || null)}
                disabled={disabled || !_selectedEntity || _entityRegions.length === 0}
                className="w-full h-8 mt-0.5 text-xs border border-slate-200 rounded-md px-2 bg-white disabled:opacity-50"
                data-testid="conditions-region-select"
              >
                <option value="">{_entityRegions.length ? '— Default —' : 'No regions on this entity'}</option>
                {_entityRegions.map(r => <option key={r} value={r}>{r}</option>)}
              </select>
              <p className="text-[10px] text-slate-400 mt-1">Applies the region's rate card and internal cost rates.</p>
            </div>
          </div>
        </div>
      )}
      {entityContext && entityContext.onCloseDateChange && (
        <div className="border border-slate-200 rounded-lg p-3 bg-slate-50/60" data-testid="conditions-close-date-section">
          <label className="text-[10px] font-medium text-slate-500 uppercase tracking-wider">Expected deal close date <span className="normal-case tracking-normal font-normal text-slate-400">(optional — assigns the deal to a financial year)</span></label>
          <input type="date" value={entityContext.closeDate || ''} onChange={e => entityContext.onCloseDateChange(e.target.value || null)} disabled={disabled}
            className="mt-1 h-8 text-xs border border-slate-200 rounded-md px-2 bg-white" data-testid="conditions-close-date-input" />
        </div>
      )}
      {/* ── Deal stage (v466) — sales probability; synced with the Won/Lost lifecycle ── */}
      {dealStage && estimateId && (
        <div className="border border-slate-200 rounded-lg p-3 bg-slate-50/60" data-testid="conditions-deal-stage-section">
          <div className="flex flex-wrap items-center gap-2">
            <label className="text-[10px] font-medium text-slate-500 uppercase tracking-wider">Deal stage <span className="normal-case tracking-normal font-normal text-slate-400">(sales probability — 100% Closed Won also marks the estimate Won)</span></label>
            <span className="ml-auto flex items-center gap-2">
              {!disabled && !dealStage.lost && (
                <label className="flex items-center gap-1 text-[10px] text-slate-500" title="Promotion date for the next stage change — defaults to today">
                  Promoted on
                  <input type="date" value={stageDate} max={new Date().toISOString().slice(0, 10)} onChange={(e) => setStageDate(e.target.value)} className="h-6 rounded border border-slate-200 px-1 text-[10px]" data-testid="conditions-deal-stage-date" />
                </label>
              )}
              <DealStageSelect estimateId={estimateId} stage={dealStage.stage} lost={dealStage.lost} canEdit={!disabled} onChange={(r) => { setStageDate(''); dealStage.onChange?.(r); }} testIdPrefix="conditions-deal-stage" effectiveAt={stageDate || null} />
            </span>
          </div>
          {dealStage.lost && <p className="mt-1 text-[10px] text-rose-600" data-testid="conditions-deal-stage-lost-note">Marked Lost — the stage is frozen until the lifecycle status changes.</p>}
          {(dealStage.history || []).length > 0 && (
            <ol className="mt-2 space-y-0.5 text-[10px] text-slate-500 max-h-28 overflow-auto" data-testid="conditions-deal-stage-history">
              {[...dealStage.history].reverse().map((h, i, arr) => {
                const idx = arr.length - 1 - i; // index in chronological history (API order)
                const next = arr[i - 1];
                const days = next ? Math.max(0, Math.round((new Date(next.at) - new Date(h.at)) / 86400000)) : Math.max(0, Math.round((Date.now() - new Date(h.at)) / 86400000));
                const editing = editIdx === idx;
                return (
                  <li key={idx} className="flex flex-wrap items-center gap-2" data-testid={`conditions-deal-stage-history-${idx}`}>
                    {editing ? (
                      <span className="flex items-center gap-1">
                        <input type="date" value={editDate} max={new Date().toISOString().slice(0, 10)} onChange={(e) => setEditDate(e.target.value)} className="h-6 rounded border border-slate-300 px-1 text-[10px]" data-testid={`conditions-deal-stage-date-input-${idx}`} />
                        <button type="button" onClick={() => saveDate(idx)} className="text-[10px] text-[#0F2040] font-semibold hover:underline" data-testid={`conditions-deal-stage-date-save-${idx}`}>Save</button>
                        <button type="button" onClick={() => setEditIdx(null)} className="text-[10px] text-slate-400 hover:underline">Cancel</button>
                      </span>
                    ) : (
                      <button type="button" disabled={disabled} onClick={() => { setEditIdx(idx); setEditDate(h.at.slice(0, 10)); }} className="w-28 shrink-0 text-left text-slate-400 hover:text-[#0F2040] disabled:hover:text-slate-400 inline-flex items-center gap-1" title={h.recorded_at ? `Date overridden${h.date_overridden_by ? ` by ${h.date_overridden_by}` : ''} — recorded ${new Date(h.recorded_at).toLocaleDateString()}. Click to change.` : 'Click to override the promotion date'} data-testid={`conditions-deal-stage-date-edit-${idx}`}>
                        {new Date(h.at).toLocaleDateString()}{!disabled && <Pencil className="w-2.5 h-2.5 opacity-60" />}{h.recorded_at && <span className="text-[9px] text-amber-600" title="Promotion date was overridden">*</span>}
                      </button>
                    )}
                    <span className="font-medium text-slate-600">{stageLabel(h.stage)}</span>
                    <span className="text-slate-400">· {h.by_name || h.by || 'system'}{h.source === 'lifecycle-won' ? ' (via Won)' : ''} · {days}d{next ? '' : ' so far'}</span>
                  </li>
                );
              })}
            </ol>
          )}
        </div>
      )}
      {/* ── Sales Lead ──
          Estimate ownership. Persists immediately when an estimateId is
          present (auditable); otherwise bubbles up for the create payload. */}
      {onSalesLeadChange && (
        <SalesLeadSelector
          estimateId={estimateId}
          value={salesLead}
          onChange={onSalesLeadChange}
          disabled={disabled}
        />
      )}
      {/* ── CS Lead ──
          Customer Success ownership. Same lifecycle/visibility/edit rules as
          the Sales Lead. */}
      {onCSLeadChange && (
        <CSLeadSelector
          estimateId={estimateId}
          value={csLead}
          onChange={onCSLeadChange}
          disabled={disabled}
        />
      )}
      {/* ── Goal / Description ──
          Free-form "what success looks like" textarea. Pre-fills the SOW's
          Customer Goal field on `Generate SOW` so PMs aren't typing the
          same goal twice. Independent of the estimate's Step-1 description
          (which is internal scoping notes); this one is stakeholder-facing
          so it travels into the SOW verbatim. */}
      <Card className="border-slate-200" data-testid="conditions-description-card">
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-[#4ABFCF]" />
            Goal / Description
          </CardTitle>
          <p className="text-xs text-slate-500 mt-1">
            What does success look like for this client? Pre-fills the SOW's <strong>Customer Goal</strong> when you generate one.
          </p>
        </CardHeader>
        <CardContent>
          <RichTextEditor
            value={v.description || ''}
            onChange={(md) => update({ description: md })}
            disabled={disabled}
            placeholder="e.g. Migrate 8,000 users off the legacy LMS by Q3 with zero data loss; deliver a self-service learning portal that improves course completion by 20%."
            rows={4}
            className="text-sm"
            data-testid="conditions-description-input"
          />
          <p className="text-[10px] text-slate-400 mt-1 text-right tabular-nums">
            {(v.description || '').replace(/[#*_>`~\-\[\]]/g, '').replace(/\s+/g, ' ').trim().length} chars
          </p>
        </CardContent>
      </Card>

      {/* ── Duration & Dates ── */}
      <Card className="border-slate-200">
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <CalendarDays className="w-4 h-4 text-[#4ABFCF]" />
            Duration & Key Dates
          </CardTitle>
          <p className="text-xs text-slate-500 mt-1">
            Stakeholder-targeted timeline. The Estimate uses <span className="font-semibold">duration as a floor</span> —
            never compresses below the effort-based projection.
          </p>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="min-w-0">
              <Label htmlFor="cond-duration" className="text-xs font-semibold text-slate-700">
                Goal Duration (weeks)
              </Label>
              <Input
                id="cond-duration"
                data-testid="conditions-goal-duration-input"
                type="number"
                min={1}
                max={156}
                value={v.goal_duration_weeks ?? ''}
                onChange={(e) => updateDuration(e.target.value ? Number(e.target.value) : null)}
                placeholder="e.g. 20"
                disabled={disabled}
                className="mt-1"
              />
              {/* v258.8 — inline floor-vs-effort hint. When the user has
                  typed a Goal Duration that is BELOW the effort-based
                  projection AND there's no explicit go-live anchor, the
                  floor logic ignores their number — explain why and how
                  to actually pin the project to the smaller duration. */}
              {(() => {
                const goal = v.goal_duration_weeks;
                const effort = meta?.effort_based_weeks;
                const anchored = !!meta?.explicit_go_live_anchor;
                if (!goal || !effort || anchored) return null;
                if (goal >= effort) return null;
                return (
                  <p
                    className="mt-1.5 text-[11px] text-amber-700 leading-snug bg-amber-50/60 border border-amber-200/70 rounded px-2 py-1.5"
                    data-testid="conditions-duration-floor-hint"
                  >
                    <strong>Floor ignored:</strong> effort needs ~{effort}w. Your {goal}w goal is below that,
                    so the system will project {effort}w. To pin the project to {goal}w, also set a
                    <strong> Target Go-Live Date</strong> — that's a hard anchor (effort overrun will be
                    flagged but go-live won't auto-shift).
                  </p>
                );
              })()}
            </div>
            <div>
              <Label htmlFor="cond-start" className="text-xs font-semibold text-slate-700">
                Target Start Date
              </Label>
              <div className="mt-1">
                <DatePickerField
                  value={v.goal_start_date || ''}
                  onChange={(iso) => updateStart(iso)}
                  testId="conditions-start-date-input"
                  placeholder="Pick start date"
                  disabled={disabled}
                  widthClass="w-full"
                />
              </div>
            </div>
            <div>
              <Label htmlFor="cond-golive" className="text-xs font-semibold text-slate-700">
                Target Go-Live Date
              </Label>
              <div className="mt-1">
                <DatePickerField
                  value={v.goal_go_live_date || ''}
                  onChange={(iso) => update({ goal_go_live_date: iso || null })}
                  testId="conditions-go-live-date-input"
                  placeholder="Pick go-live date"
                  minDate={v.goal_start_date || undefined}
                  anchorDate={v.goal_start_date || undefined}
                  quickRanges
                  disabled={disabled}
                  widthClass="w-full"
                />
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* ── Hypercare & Holiday Region ── */}
      <Card className="border-slate-200">
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <LifeBuoy className="w-4 h-4 text-[#4ABFCF]" />
            Hypercare & Holiday Region
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="min-w-0">
              <Label htmlFor="cond-hypercare" className="text-xs font-semibold text-slate-700 block leading-tight min-h-[2.25rem]">
                Hypercare Weeks <span className="text-slate-500 font-normal">(post Go-Live)</span>
              </Label>
              <Input
                id="cond-hypercare"
                data-testid="conditions-hypercare-weeks-input"
                type="number"
                min={0}
                max={52}
                value={v.hypercare_weeks ?? ''}
                onChange={(e) => update({
                  hypercare_weeks: e.target.value ? Number(e.target.value) : null,
                })}
                placeholder="e.g. 4"
                disabled={disabled}
                className="mt-1"
              />
              <p className="text-[11px] text-slate-500 mt-1.5 leading-snug">
                Overrides default Hypercare duration on the Planner.
              </p>
            </div>
            <div className="min-w-0">
              <Label className="text-xs font-semibold text-slate-700 flex items-center gap-1 leading-tight min-h-[2.25rem]">
                <Globe2 className="w-3 h-3 shrink-0" />
                <span>Holiday Region</span>
              </Label>
              <Select
                value={region}
                onValueChange={(val) => update({
                  holiday_region: val === 'NONE' ? null : val,
                })}
                disabled={disabled}
              >
                <SelectTrigger
                  data-testid="conditions-holiday-region-select"
                  className="mt-1"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {HOLIDAY_REGIONS.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-[11px] text-slate-500 mt-1.5 leading-snug">
                Public holidays render as faint bands on the read-only Planner.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* ── Breaks (paused work windows) ── */}
      <BreaksListEditor
        items={v.breaks || []}
        onAdd={() => addItem('breaks')}
        onUpdate={(idx, field, val) => updateItem('breaks', idx, field, val)}
        onRemove={(idx) => removeItem('breaks', idx)}
        disabled={disabled}
        availableComponents={availableComponents}
        highlightBreakId={highlightBreakId}
        holidayRegion={region}
        projectYear={getProjectYear(v)}
        goalDurationWeeks={v.goal_duration_weeks}
      />

      {/* ── Blackouts (no-deploy windows) ── */}
      <DateRangeListEditor
        title="Blackouts"
        subtitle="No-deployment windows — work continues, but Go-Live cannot land inside (e.g. Black Friday, fiscal close)."
        icon={<Ban className="w-4 h-4 text-rose-600" />}
        accent="rose"
        items={v.blackouts || []}
        kind="blackouts"
        onAdd={() => addItem('blackouts')}
        onUpdate={(idx, field, val) => updateItem('blackouts', idx, field, val)}
        onRemove={(idx) => removeItem('blackouts', idx)}
        disabled={disabled}
        goalDurationWeeks={v.goal_duration_weeks}
      />
    </div>
  );
}


// Aggregates `computeRangeDuration` across a list of rows so the
// freeze/blackout cards can show a one-line total at the top:
// `Total: 23 working days · 4.6 cal weeks across 3 breaks`. Skips rows
// where dates are missing/invalid (those have no chip either).
const computeListRollup = (items) => {
  if (!Array.isArray(items) || items.length === 0) return null;
  let totalWorking = 0;
  let totalCal = 0;
  let counted = 0;
  for (const item of items) {
    const dur = computeRangeDuration(item.start_date, item.end_date);
    if (!dur) continue;
    totalWorking += dur.workingDays;
    totalCal += dur.calDays;
    counted += 1;
  }
  if (counted === 0) return null;
  const weeks = totalCal / 7;
  const secondary = totalCal >= 10
    ? `${weeks % 1 === 0 ? weeks : weeks.toFixed(1)} cal weeks`
    : `${totalCal} cal days`;
  return {
    counted,
    totalWorking,
    totalCal,
    primary: `${totalWorking} working day${totalWorking === 1 ? '' : 's'}`,
    secondary,
  };
};


/**
 * BreaksListEditor — like DateRangeListEditor but with a per-break
 * "Affects" selector (all / none / specific) and an optional component
 * multi-select when 'specific' is chosen. The user controls which
 * components honor each break — this drives the project-extension math
 * server-side and the per-component bar splits in the Gantt.
 */
function BreaksListEditor({ items, onAdd, onUpdate, onRemove, disabled, availableComponents, highlightBreakId, holidayRegion, projectYear, goalDurationWeeks }) {
  const rowRefs = React.useRef({});
  const [pulseId, setPulseId] = React.useState(null);
  const rollup = computeListRollup(items);
  // % of overall project duration (calendar weeks). Hidden when the
  // goal is missing/zero — we don't want to show "Infinity%" or "NaN%"
  // edge cases in front of a PM.
  const goalWeeks = typeof goalDurationWeeks === 'number' && goalDurationWeeks > 0
    ? goalDurationWeeks : null;
  const rollupPct = (rollup && goalWeeks) ? Math.round((rollup.totalCal / 7 / goalWeeks) * 100) : null;
  React.useEffect(() => {
    if (!highlightBreakId) return undefined;
    const el = rowRefs.current[highlightBreakId];
    if (!el) return undefined;
    el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    setPulseId(highlightBreakId);
    const t = setTimeout(() => setPulseId(null), 2200);
    return () => clearTimeout(t);
  }, [highlightBreakId, items.length]);

  // Apply a curated suggestion to a break row in one shot — fills
  // label + start_date + end_date so the user can hit Save without
  // touching the date pickers themselves.
  const applySuggestion = (idx, suggestion) => {
    onUpdate(idx, 'label', suggestion.label);
    onUpdate(idx, 'start_date', suggestion.start_date);
    onUpdate(idx, 'end_date', suggestion.end_date);
  };
  return (
    <Card className="border-slate-200">
      <CardHeader className="pb-3 flex flex-row items-start justify-between space-y-0">
        <div>
          <CardTitle className="text-base flex items-center gap-2">
            <CalendarDays className="w-4 h-4 text-amber-600" />
            Breaks
            <Badge variant="outline" className="text-[10px] bg-amber-50 text-amber-700 border-amber-200">
              {items.length}
            </Badge>
            {rollup && (
              <span
                className="inline-flex items-center gap-1.5 ml-1 px-2 py-0.5 rounded-full text-[10px] font-medium bg-amber-50 text-amber-800 border border-amber-200"
                title={`Across ${rollup.counted} break${rollup.counted === 1 ? '' : 's'} with both dates set${rollupPct !== null ? ` · ~${rollupPct}% of the ${goalWeeks}-week plan` : ''}`}
                data-testid="conditions-breaks-rollup"
              >
                Total <span className="font-semibold tabular-nums">{rollup.primary}</span>
                <span className="text-amber-500">·</span>
                <span className="tabular-nums">{rollup.secondary}</span>
                {rollupPct !== null && (
                  <>
                    <span className="text-amber-500">·</span>
                    <span className={`font-semibold tabular-nums ${rollupPct >= 30 ? 'text-rose-600' : ''}`} data-testid="conditions-breaks-rollup-pct">
                      ~{rollupPct}% of plan
                    </span>
                  </>
                )}
              </span>
            )}
          </CardTitle>
          <p className="text-xs text-slate-500 mt-1">
            Scheduled pauses — no work occurs during these windows. Each break
            can apply to <span className="font-semibold">all components</span>,
            <span className="font-semibold"> none</span> (calendar-only), or
            <span className="font-semibold"> specific components</span>.
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={onAdd}
          disabled={disabled}
          data-testid="conditions-add-breaks-btn"
        >
          <Plus className="w-3.5 h-3.5 mr-1" />
          Add
        </Button>
      </CardHeader>
      <CardContent>
        {items.length === 0 ? (
          <div className="py-6 text-center text-xs text-slate-400 border-2 border-dashed border-slate-200 rounded-lg">
            No breaks configured.
          </div>
        ) : (
          <div className="space-y-2" data-testid="conditions-breaks-list">
            {items.map((item, idx) => {
              const applies = item.applies_to || 'all';
              const compNames = item.component_names || [];
              return (
                <div
                  key={item.id}
                  ref={(el) => { if (el) rowRefs.current[item.id] = el; }}
                  className={`bg-slate-50 border rounded-lg p-3 space-y-2 transition-shadow ${
                    pulseId === item.id
                      ? 'border-amber-400 ring-4 ring-amber-200/70 animate-pulse'
                      : 'border-slate-200'
                  }`}
                  data-testid={`conditions-breaks-row-${idx}`}
                >
                  <div className="flex flex-wrap items-end gap-2">
                    <div className="flex-1 min-w-[160px] basis-[160px]">
                      <Label className="text-[11px] font-semibold text-slate-600">Label</Label>
                      <Input
                        placeholder="e.g. Easter Freeze"
                        value={item.label || ''}
                        onChange={(e) => onUpdate(idx, 'label', e.target.value)}
                        disabled={disabled}
                        className="mt-1 h-8 text-sm"
                        data-testid={`conditions-breaks-label-input-${idx}`}
                      />
                    </div>
                    <div className="w-[140px] shrink-0">
                      <Label className="text-[11px] font-semibold text-slate-600">Start</Label>
                      <div className="mt-1">
                        <DatePickerField
                          value={item.start_date || ''}
                          onChange={(iso) => onUpdate(idx, 'start_date', iso)}
                          disabled={disabled}
                          testId={`conditions-breaks-start-input-${idx}`}
                          placeholder="Start"
                          widthClass="w-full"
                        />
                      </div>
                    </div>
                    <div className="w-[140px] shrink-0">
                      <Label className="text-[11px] font-semibold text-slate-600">End</Label>
                      <div className="mt-1">
                        <DatePickerField
                          value={item.end_date || ''}
                          onChange={(iso) => onUpdate(idx, 'end_date', iso)}
                          disabled={disabled}
                          testId={`conditions-breaks-end-input-${idx}`}
                          placeholder="End"
                          minDate={item.start_date || undefined}
                          anchorDate={item.start_date || undefined}
                          quickWeeks
                          widthClass="w-full"
                        />
                      </div>
                    </div>
                    <div className="flex-1 min-w-[140px] basis-[140px]">
                      <Label className="text-[11px] font-semibold text-slate-600">Affects</Label>
                      <Select
                        value={applies}
                        onValueChange={(val) => {
                          onUpdate(idx, 'applies_to', val);
                          if (val !== 'specific') onUpdate(idx, 'component_names', []);
                        }}
                        disabled={disabled}
                      >
                        <SelectTrigger
                          className="mt-1 h-8 text-sm"
                          data-testid={`conditions-breaks-applies-${idx}`}
                        >
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">All components</SelectItem>
                          <SelectItem value="none">None (calendar only)</SelectItem>
                          <SelectItem value="specific">Specific components…</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="flex items-end gap-1 shrink-0 ml-auto">
                      <SuggestDatesButton
                        idx={idx}
                        region={holidayRegion}
                        year={projectYear}
                        onApply={(s) => applySuggestion(idx, s)}
                        disabled={disabled}
                      />
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => onRemove(idx)}
                        disabled={disabled}
                        className="text-rose-600 hover:text-rose-700 hover:bg-rose-50 h-8 px-2"
                        data-testid={`conditions-breaks-remove-btn-${idx}`}
                        title="Remove this break"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  </div>

                  {(() => {
                    const dur = computeRangeDuration(item.start_date, item.end_date);
                    if (!dur) return null;
                    return (
                      <div
                        className="flex items-center gap-1.5 mt-1 text-[10px] text-slate-500"
                        data-testid={`conditions-breaks-duration-${idx}`}
                      >
                        <CalendarDays className="w-3 h-3 text-slate-400" />
                        <span className="font-semibold tabular-nums text-slate-700">{dur.primary}</span>
                        <span className="text-slate-400">·</span>
                        <span className="tabular-nums">{dur.secondary}</span>
                      </div>
                    );
                  })()}

                  {applies === 'specific' && (
                    <ComponentMultiSelect
                      available={availableComponents}
                      selected={compNames}
                      onChange={(next) => onUpdate(idx, 'component_names', next)}
                      disabled={disabled}
                      testidPrefix={`conditions-breaks-components-${idx}`}
                    />
                  )}
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
}


/**
 * Compact multi-select rendered as toggle chips. We avoid a heavy combobox
 * since most estimates have <30 components and chips give a clear overview.
 */
function ComponentMultiSelect({ available, selected, onChange, disabled, testidPrefix }) {
  const set = new Set(selected || []);
  const toggle = (name) => {
    const next = new Set(set);
    if (next.has(name)) next.delete(name); else next.add(name);
    onChange([...next]);
  };
  if (!available || available.length === 0) {
    return (
      <p
        className="text-[11px] text-slate-500 italic px-1"
        data-testid={`${testidPrefix}-empty`}
      >
        Component list unavailable here — toggle this break's exemptions from the Estimator's component list.
      </p>
    );
  }
  return (
    <div className="flex flex-wrap gap-1.5 pt-1" data-testid={testidPrefix}>
      {available.map((name) => {
        const on = set.has(name);
        return (
          <button
            key={name}
            type="button"
            onClick={() => toggle(name)}
            disabled={disabled}
            className={`text-[11px] px-2 py-0.5 rounded-full border transition-colors ${
              on
                ? 'bg-amber-100 text-amber-800 border-amber-300'
                : 'bg-white text-slate-600 border-slate-200 hover:border-amber-300'
            }`}
            data-testid={`${testidPrefix}-chip-${name}`}
          >
            {on ? '✓ ' : ''}{name}
          </button>
        );
      })}
    </div>
  );
}

function DateRangeListEditor({
  title, subtitle, icon, accent, items, kind, onAdd, onUpdate, onRemove, disabled, goalDurationWeeks,
}) {
  const accentBadge = accent === 'amber'
    ? 'bg-amber-50 text-amber-700 border-amber-200'
    : 'bg-rose-50 text-rose-700 border-rose-200';
  const rollupBadge = accent === 'amber'
    ? 'bg-amber-50 text-amber-800 border-amber-200'
    : 'bg-rose-50 text-rose-800 border-rose-200';
  const rollupAccent = accent === 'amber' ? 'text-amber-500' : 'text-rose-400';
  const rollup = computeListRollup(items);
  const goalWeeks = typeof goalDurationWeeks === 'number' && goalDurationWeeks > 0
    ? goalDurationWeeks : null;
  const rollupPct = (rollup && goalWeeks) ? Math.round((rollup.totalCal / 7 / goalWeeks) * 100) : null;

  return (
    <Card className="border-slate-200">
      <CardHeader className="pb-3 flex flex-row items-start justify-between space-y-0">
        <div>
          <CardTitle className="text-base flex items-center gap-2 flex-wrap">
            {icon}
            {title}
            <Badge variant="outline" className={`text-[10px] ${accentBadge}`}>
              {items.length}
            </Badge>
            {rollup && (
              <span
                className={`inline-flex items-center gap-1.5 ml-1 px-2 py-0.5 rounded-full text-[10px] font-medium border ${rollupBadge}`}
                title={`Across ${rollup.counted} ${title.toLowerCase()} with both dates set${rollupPct !== null ? ` · ~${rollupPct}% of the ${goalWeeks}-week plan` : ''}`}
                data-testid={`conditions-${kind}-rollup`}
              >
                Total <span className="font-semibold tabular-nums">{rollup.primary}</span>
                <span className={rollupAccent}>·</span>
                <span className="tabular-nums">{rollup.secondary}</span>
                {rollupPct !== null && (
                  <>
                    <span className={rollupAccent}>·</span>
                    <span className={`font-semibold tabular-nums ${rollupPct >= 30 ? 'text-rose-600' : ''}`} data-testid={`conditions-${kind}-rollup-pct`}>
                      ~{rollupPct}% of plan
                    </span>
                  </>
                )}
              </span>
            )}
          </CardTitle>
          <p className="text-xs text-slate-500 mt-1">{subtitle}</p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={onAdd}
          disabled={disabled}
          data-testid={`conditions-add-${kind}-btn`}
        >
          <Plus className="w-3.5 h-3.5 mr-1" />
          Add
        </Button>
      </CardHeader>
      <CardContent>
        {items.length === 0 ? (
          <div className="py-6 text-center text-xs text-slate-400 border-2 border-dashed border-slate-200 rounded-lg">
            No {title.toLowerCase()} configured.
          </div>
        ) : (
          <div className="space-y-2" data-testid={`conditions-${kind}-list`}>
            {items.map((item, idx) => (
              <div
                key={item.id}
                className="bg-slate-50 border border-slate-200 rounded-lg p-3"
                data-testid={`conditions-${kind}-row-${idx}`}
              >
                <div className="flex flex-wrap items-end gap-2">
                <div className="flex-1 min-w-[160px] basis-[160px]">
                  <Label className="text-[11px] font-semibold text-slate-600">Label</Label>
                  <Input
                    placeholder="e.g. Easter Freeze"
                    value={item.label || ''}
                    onChange={(e) => onUpdate(idx, 'label', e.target.value)}
                    disabled={disabled}
                    className="mt-1 h-8 text-sm"
                    data-testid={`conditions-${kind}-label-input-${idx}`}
                  />
                </div>
                <div className="w-[140px] shrink-0">
                  <Label className="text-[11px] font-semibold text-slate-600">Start</Label>
                  <div className="mt-1">
                    <DatePickerField
                      value={item.start_date || ''}
                      onChange={(iso) => onUpdate(idx, 'start_date', iso)}
                      disabled={disabled}
                      testId={`conditions-${kind}-start-input-${idx}`}
                      placeholder="Start"
                      widthClass="w-full"
                    />
                  </div>
                </div>
                <div className="w-[140px] shrink-0">
                  <Label className="text-[11px] font-semibold text-slate-600">End</Label>
                  <div className="mt-1">
                    <DatePickerField
                      value={item.end_date || ''}
                      onChange={(iso) => onUpdate(idx, 'end_date', iso)}
                      disabled={disabled}
                      testId={`conditions-${kind}-end-input-${idx}`}
                      placeholder="End"
                      minDate={item.start_date || undefined}
                      anchorDate={item.start_date || undefined}
                      quickWeeks
                      widthClass="w-full"
                    />
                  </div>
                </div>
                <div className="flex items-end shrink-0 ml-auto">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => onRemove(idx)}
                    disabled={disabled}
                    className="text-rose-600 hover:text-rose-700 hover:bg-rose-50 h-8 px-2.5"
                    data-testid={`conditions-${kind}-remove-btn-${idx}`}
                    title="Remove"
                  >
                    <Trash2 className="w-3.5 h-3.5 mr-1.5" />
                    Remove
                  </Button>
                </div>
                </div>
                {(() => {
                  const dur = computeRangeDuration(item.start_date, item.end_date);
                  if (!dur) return null;
                  return (
                    <div
                      className="flex items-center gap-1.5 mt-2 text-[10px] text-slate-500"
                      data-testid={`conditions-${kind}-duration-${idx}`}
                    >
                      <CalendarDays className="w-3 h-3 text-slate-400" />
                      <span className="font-semibold tabular-nums text-slate-700">{dur.primary}</span>
                      <span className="text-slate-400">·</span>
                      <span className="tabular-nums">{dur.secondary}</span>
                    </div>
                  );
                })()}
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}



/**
 * SuggestDatesButton — popover trigger that lists curated freeze/holiday
 * ranges for the estimate's `holiday_region`, year-aware to the project's
 * goal start. Click an entry → fills label + start_date + end_date in
 * one go so PMs don't have to look up calendars manually.
 *
 * Hidden when no region is selected (NONE) — falls back to a tiny disabled
 * affordance with a tooltip explaining why.
 */
function SuggestDatesButton({ idx, region, year, onApply, disabled }) {
  const [open, setOpen] = React.useState(false);
  const isNoRegion = !region || region === 'NONE';
  const suggestions = React.useMemo(
    () => (isNoRegion ? [] : getRegionSuggestions(region, year)),
    [isNoRegion, region, year],
  );

  if (isNoRegion) {
    return (
      <Button
        variant="ghost"
        size="sm"
        disabled
        className="h-8 px-2 text-slate-400"
        title="Pick a Holiday Region above to enable date suggestions"
        data-testid={`conditions-breaks-suggest-btn-${idx}`}
      >
        <Sparkles className="w-3.5 h-3.5" />
      </Button>
    );
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          disabled={disabled}
          className="h-8 px-2 text-amber-700 hover:bg-amber-50"
          title={`Suggest dates for ${region} (${year})`}
          data-testid={`conditions-breaks-suggest-btn-${idx}`}
        >
          <Sparkles className="w-3.5 h-3.5" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        className="w-72 p-1.5"
        data-testid={`conditions-breaks-suggest-popover-${idx}`}
      >
        <div className="px-2 py-1 mb-1 border-b border-slate-100">
          <p className="text-[11px] font-semibold text-slate-700">
            Suggested freezes — {region} {year}
          </p>
          <p className="text-[10px] text-slate-400 leading-tight">
            Click to fill label + start + end. You can edit afterwards.
          </p>
        </div>
        <div className="max-h-72 overflow-y-auto">
          {suggestions.map((s, i) => (
            <button
              key={`${s.label}-${i}`}
              onClick={() => { onApply(s); setOpen(false); }}
              className="w-full text-left px-2 py-1.5 rounded-md hover:bg-amber-50 transition-colors group"
              data-testid={`conditions-breaks-suggest-item-${idx}-${i}`}
            >
              <div className="text-xs font-medium text-slate-800 group-hover:text-amber-800">
                {s.label}
              </div>
              <div className="text-[10px] text-slate-500 font-mono">
                {s.start_date} → {s.end_date}
              </div>
            </button>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}
